import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { User } from 'app/core/user/user.types';
import { catchError, map, Observable, of, ReplaySubject, tap } from 'rxjs';
import {AuthService, TokenPayload} from 'app/core/auth/password-grant/auth.service';
import { environment } from 'environments/environment';
import { UsuarioModel } from 'app/models/usuarios/usuarios.model';

@Injectable({providedIn: 'root'})
export class UserService
{
    private _httpClient = inject(HttpClient);
    private _user: ReplaySubject<User> = new ReplaySubject<User>(1);
    private auth: AuthService = inject(AuthService);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User)
    {
        // Store the value
        this._user.next(value);
    }

    get user$(): Observable<User>
    {
        return this._user.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current signed-in user data
     */
    get(): Observable<User>
    {
        const payload = this.auth.tokenPayload;
        const userId = payload?.sub;
        const fallback = this.buildFallbackUser(payload, userId);

        const source$ = userId
            ? this.fetchUser(userId, fallback)
            : of(fallback);

        return source$.pipe(tap((user) => this._user.next(user)));
    }

    private fetchUser(userId: string, fallback: User): Observable<User> {
        return this._httpClient
            .get<UsuarioModel>(`${environment.serverUrl}api/keycloak/usuarios/${userId}`)
            .pipe(
                map((resp) => this.mapApiUserToUser(resp, userId, fallback)),
                catchError(() => of(fallback))
            );
    }

    private buildFallbackUser(payload: TokenPayload, userId?: string): User {
        return {
            id: userId ?? '',
            name: payload?.name ?? payload?.preferred_username ?? 'Usuário',
            email: payload?.email ?? '',
            avatar: undefined,
            status: 'online',
        };
    }

    private mapApiUserToUser(resp: UsuarioModel, userId: string, fallback: User): User {
        const fullName = this.buildFullName(resp);

        return {
            id: userId,
            name: fullName || resp.username || fallback.name,
            email: resp.email || fallback.email,
            avatar: resp.fotoPerfil || fallback.avatar,
            status: 'online',
        };
    }

    private buildFullName(resp: UsuarioModel): string {
        return `${resp.firstName || ''} ${resp.lastName || ''}`.trim();
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: User): Observable<any>
    {
        return this._httpClient.patch<User>('api/common/user', {user}).pipe(
            map((response) =>
            {
                this._user.next(response);
            }),
        );
    }
}
